import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CalendarPicker from '../util/CalendarPicker';

interface Props {
  onClose: () => void;
  onInvited?: () => void;
}

const EMAIL_RE = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]).{8,}$/;

type Mode = 'link' | 'manual';

const InviteUserModal: React.FC<Props> = ({ onClose, onInvited }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [email, setEmail] = useState('');
  const [patternId, setPatternId] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [patterns, setPatterns] = useState<{ id: number; name: string; hoursPerWeek: number; isDefault?: boolean }[]>([]);
  const [departments, setDepartments] = useState<{ id: number; title: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<Mode>('link');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  useEffect(() => {
    Promise.allSettled([axios.get('hr-schedule-template'), axios.get('department')]).then(([p, d]) => {
      if (p.status === 'fulfilled') {
        const list = Array.isArray(p.value.data) ? p.value.data : [];
        setPatterns(list);
        const def = list.find((x: any) => x.isDefault) || list[0];
        if (def) setPatternId(String(def.id));
      }
      if (d.status === 'fulfilled') setDepartments((Array.isArray(d.value.data) ? d.value.data : []).filter((x: any) => x.active !== false));
    });
  }, []);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !patternId || !contractDate) {
      enqueueSnackbar(t('FillRequired') as string, { variant: 'warning' }); return;
    }
    if (!EMAIL_RE.test(email)) { enqueueSnackbar(t('InvalidEmail') || 'Invalid email', { variant: 'error' }); return; }
    if (mode === 'manual') {
      if (!password || !passwordConfirm) { enqueueSnackbar('Enter password and confirm', { variant: 'error' }); return; }
      if (password !== passwordConfirm) { enqueueSnackbar('Passwords do not match', { variant: 'error' }); return; }
      if (!PASSWORD_RE.test(password)) { enqueueSnackbar(t('PasswordRequirements'), { variant: 'error' }); return; }
    }
    setSaving(true);
    try {
      const payload: any = {
        first_name: firstName, last_name: lastName, second_name: secondName || undefined,
        email, patternId: Number(patternId), contract_start_date: contractDate,
        departmentId: departmentId ? Number(departmentId) : undefined,
      };
      if (mode === 'manual') payload.password = password;

      const { data } = await axios.post('invite-user', payload);

      if (mode === 'manual') {
        enqueueSnackbar('User created and ready to log in', { variant: 'success' });
      } else if (data?.mailSent === false) {
        enqueueSnackbar(t('InviteCreatedNoMail') || 'User created, but the invitation email was not sent — check the company SMTP settings.', { variant: 'warning', autoHideDuration: 6000 });
      } else {
        enqueueSnackbar(t('InviteSent') || 'Invitation link sent', { variant: 'success' });
      }
      onInvited?.(); onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || (t('Error') as string), { variant: 'error' });
    } finally { setSaving(false); }
  };

  const lbl: React.CSSProperties = { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-color-calendar-week)' };
  const inp = 'timesheet-edit-input';
  const fld: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

  const modeBtn = (m: Mode, icon: string, title: string, desc: string) => (
    <button type="button" onClick={() => setMode(m)} style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3,
      padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
      border: mode === m ? '2px solid #4c1d95' : '2px solid var(--border2-new, rgba(0,10,60,.08))',
      background: mode === m ? 'rgba(76,29,149,.07)' : 'var(--surface2)',
      transition: 'border-color .15s, background .15s',
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color)' }}>{title}</span>
      <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', lineHeight: 1.4 }}>{desc}</span>
    </button>
  );

  return (
    <div data-portal-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)' as any, background: 'rgba(10,15,35,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: 460, maxWidth: '100%', maxHeight: '88vh', background: 'var(--bg-form)', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(20,30,70,.3)' }}>

        {/* Header */}
        <div style={{ padding: '14px 18px', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg,#1e3a8a,#4c1d95 60%,#831843)' }}>
          <span style={{ fontSize: 18 }}>👤</span>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 800 }}>{t('AddUser') || 'Add user'}</div>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: 8, color: '#fff', width: 28, height: 28, cursor: 'pointer' }}>✕</button>
        </div>

        <div className="thin-scroll" style={{ padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 11 }}>
          {/* Name row */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('FirstName')} <span style={{ color: '#ef4444' }}>*</span></span>
              <input className={inp} value={firstName} onChange={e => setFirstName(e.target.value)} /></div>
            <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('LastName')} <span style={{ color: '#ef4444' }}>*</span></span>
              <input className={inp} value={lastName} onChange={e => setLastName(e.target.value)} /></div>
          </div>
          <div style={fld}><span style={lbl}>{t('SecondName')}</span><input className={inp} value={secondName} onChange={e => setSecondName(e.target.value)} /></div>
          <div style={fld}><span style={lbl}>{t('Email')} <span style={{ color: '#ef4444' }}>*</span></span>
            <input className={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('HrWorkingPattern')} <span style={{ color: '#ef4444' }}>*</span></span>
              <select title={t('HrWorkingPattern') as string} className={inp} value={patternId} onChange={e => setPatternId(e.target.value)}>
                {patterns.map(p => <option key={p.id} value={p.id}>{p.name} ({p.hoursPerWeek}h){p.isDefault ? ' ★' : ''}</option>)}
              </select></div>
            <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('ContractStartDate')} <span style={{ color: '#ef4444' }}>*</span></span>
              <CalendarPicker mode="single" variant="field" output="string" value={contractDate} onChange={(v) => setContractDate(v)} /></div>
          </div>
          <div style={fld}><span style={lbl}>{t('Department')}</span>
            <select title={t('Department') as string} className={inp} value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
              <option value="">—</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select></div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--border2-new, rgba(0,10,60,.06))', margin: '2px 0' }} />

          {/* Mode selector */}
          <div>
            <span style={{ ...lbl, display: 'block', marginBottom: 8 }}>Activation method</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {modeBtn('link', '✉️', 'Send activation link', 'Email with a link to set password is sent to the user')}
              {modeBtn('manual', '🔑', 'Set password manually', 'You set the password — user can log in right away')}
            </div>
          </div>

          {/* Manual password fields */}
          {mode === 'manual' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...fld, flex: 1 }}>
                <span style={lbl}>Password <span style={{ color: '#ef4444' }}>*</span></span>
                <input className={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 chars, A-z, symbol" />
              </div>
              <div style={{ ...fld, flex: 1 }}>
                <span style={lbl}>Confirm <span style={{ color: '#ef4444' }}>*</span></span>
                <input className={inp} type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} placeholder="Repeat password" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '11px 16px', borderTop: '1px solid var(--border2-new, rgba(0,10,60,.06))', display: 'flex', justifyContent: 'flex-end', gap: 8, background: 'var(--surface2)' }}>
          <button type="button" className="dt-btn-ghost" style={{ fontSize: 13 }} onClick={onClose}>{t('Cancel')}</button>
          <button type="button" className="btn-primary" style={{ fontSize: 13 }} disabled={saving} onClick={submit}>
            {mode === 'link' ? `✉️ ${t('SendInvite') || 'Send invite'}` : `✅ ${t('AddUser') || 'Add user'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
